import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  FileSearch,
  Loader2,
  MapPin,
  RotateCcw,
  Search,
  Star,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import RefreshButton from "../components/RefreshButton";
import api, { getAllPages, logError } from "../services/api";
import {
  TRIP_STATUS_CLASSES as statusClasses,
  buildRidersMap,
  formatTripDateTime,
  isWithinTimeFilter,
  normalizeTrip,
} from "../utils/tripLog";

const PAGE_SIZE = 20;
// The statuses a booking can really have. "Expired" is a cancelled booking
// whose reason is expired_unclaimed (see normalizeTrip).
const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "requested", label: "Requested" },
  { value: "accepted", label: "Accepted" },
  { value: "active", label: "In progress" },
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
];
const TIME_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
];

// Statuses support is allowed to cancel. Kept deliberately loose because the
// backend is the real authority — it refuses completed, already-cancelled and
// awaiting-payment bookings and returns a readable 400, which we surface. This
// list only decides whether the button is worth showing at all.
const CANCELLABLE_STATUSES = new Set([
  "requested",
  "pending",
  "accepted",
  "active",
  "in_progress",
]);

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Unable to load trip logs.";

// The cancel endpoint's 400s are written to be read by a human ("Cannot cancel
// a booking awaiting payment..."), so surface them verbatim rather than a
// generic failure. class-validator returns an array of messages.
const getCancelErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(". ");
  return message || error?.message || "Unable to cancel this trip.";
};

const getSearchText = (trip) =>
  [
    trip.riderName,
    trip.customerName,
    trip.id,
    trip.shortId,
    trip.pickup,
    trip.dropoff,
    trip.payment,
    trip.paymentMethod,
    trip.rating,
    trip.tip,
    trip.statusLabel,
    trip.cancellation?.text,
  ]
    .join(" ")
    .toLowerCase();

const TableHeader = () => (
  <thead className="sticky top-0 z-10 bg-gray-50/95 text-left dark:bg-gray-800/95">
    <tr className="border-b border-gray-200 dark:border-gray-700">
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">ID</th>
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">Passenger</th>
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">Time passenger requested</th>
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">Rider that accepted</th>
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">Arrival time</th>
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">Completion time</th>
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">Time passenger paid</th>
      <th scope="col" className="whitespace-nowrap px-4 py-3 font-semibold">Rating given</th>
      {/* Pinned right so Cancel stays in reach on a phone. */}
      <th scope="col" className="sticky right-0 whitespace-nowrap bg-gray-50 px-4 py-3 text-right font-semibold dark:bg-gray-800 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">Actions</th>
    </tr>
  </thead>
);

const SkeletonRows = () => (
  <tbody aria-label="Loading trip logs">
    {Array.from({ length: 6 }, (_, index) => (
      <tr
        key={`trip-skeleton-${index}`}
        className="animate-pulse border-b border-gray-100 dark:border-gray-800"
      >
        {Array.from({ length: 9 }, (_, cellIndex) => (
          <td key={`trip-skeleton-${index}-${cellIndex}`} className="px-4 py-4">
            <div
              className={`h-3 rounded bg-gray-200 dark:bg-gray-700 ${
                cellIndex === 2 ? "w-40" : cellIndex === 7 ? "w-28" : "w-24"
              }`}
            />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

const EmptyState = ({ filtered }) => (
  <div className="flex min-h-64 flex-col items-center justify-center px-6 py-12 text-center">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF5E6] text-[#FF8C00] dark:bg-orange-400/10">
      <MapPin className="h-6 w-6" aria-hidden="true" />
    </div>
    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
      {filtered ? "No trips found" : "No ride records are available yet."}
    </h2>
    <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
      {filtered
        ? "No trips found for the selected filters. Try changing the search or filters."
        : "New rides will appear here once customers start booking."}
    </p>
  </div>
);

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [retryToken, setRetryToken] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Cancellation flow. cancelTarget doubles as "is the dialog open".
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(null);

  const openCancelDialog = (trip) => {
    setCancelTarget(trip);
    setCancelReason("");
    setCancelError(null);
  };

  const closeCancelDialog = () => {
    if (cancelling) return; // don't let a click dismiss an in-flight request
    setCancelTarget(null);
    setCancelReason("");
    setCancelError(null);
  };

  const confirmCancel = async () => {
    if (!cancelTarget?.id || cancelTarget.id === "N/A") {
      setCancelError("This trip has no booking id, so it cannot be cancelled.");
      return;
    }

    setCancelling(true);
    setCancelError(null);

    try {
      const reason = cancelReason.trim();
      await api.post(`/v1/admin/rides/${cancelTarget.id}/cancel`, {
        // Omit rather than send an empty string — the backend then records its
        // own "Cancelled by support" default instead of a blank reason.
        ...(reason ? { reason } : {}),
      });

      setCancelSuccess(
        `Cancelled ${cancelTarget.shortId} — ${cancelTarget.customerName}. Both the passenger and the rider have been notified.`,
      );
      setCancelTarget(null);
      setCancelReason("");
      // Re-fetch so the row reflects its new status rather than going stale.
      setRetryToken((token) => token + 1);
    } catch (cancelException) {
      setCancelError(getCancelErrorMessage(cancelException));
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTrips = async () => {
      setLoading(true);
      setError(null);

      try {
        // The bookings endpoint can't filter (it 400s on ?status=), so filters
        // and search run here over every booking, not over one page.
        const [records, riderRecords] = await Promise.all([
          getAllPages("/v1/admin/bookings"),
          getAllPages("/v1/admin/riders").catch((riderError) => {
            logError("Unable to load rider names for trip logs:", riderError);
            return [];
          }),
        ]);
        const ridersMap = buildRidersMap(riderRecords);
        const normalizedTrips = records.map((booking) => normalizeTrip(booking, ridersMap));

        if (!isMounted) return;

        setTrips(normalizedTrips);
        setUpdatedAt(new Date());
      } catch (fetchError) {
        if (!isMounted) return;
        // Keep the rows already on screen; the banner says the refresh failed.
        setError(getErrorMessage(fetchError));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTrips();
    return () => {
      isMounted = false;
    };
  }, [retryToken]);

  const filteredTrips = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return trips.filter((trip) => {
      const matchesSearch = !normalizedSearch || getSearchText(trip).includes(normalizedSearch);
      const matchesStatus = statusFilter === "all" || trip.status === statusFilter;
      const matchesTime = isWithinTimeFilter(trip.requestedAt, timeFilter);
      return matchesSearch && matchesStatus && matchesTime;
    });
  }, [searchTerm, statusFilter, timeFilter, trips]);

  const totalPages = Math.max(1, Math.ceil(filteredTrips.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const firstIndex = (currentPage - 1) * PAGE_SIZE;
  const pageTrips = filteredTrips.slice(firstIndex, firstIndex + PAGE_SIZE);

  const updateFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
  };

  const handleRetry = () => {
    setError(null);
    setRetryToken((current) => current + 1);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) return;
    setPage(nextPage);
  };

  const hasApiRecords = trips.length > 0;
  const hasFilteredRecords = filteredTrips.length > 0;
  const totalLabel = trips.length.toLocaleString();

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar />
      <div className="flex w-full flex-1 flex-col lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6" aria-busy={loading}>
          <div className="mx-auto max-w-[1600px] space-y-5">
            <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                    Trip Logs
                  </h1>
                  <span className="rounded-full bg-[#FFF5E6] px-3 py-1 text-xs font-semibold text-[#FF8C00] dark:bg-orange-400/10 dark:text-orange-300">
                    {totalLabel} Trips
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Track every ride from request to completion.
                </p>
              </div>
              <RefreshButton onRefresh={handleRetry} loading={loading} updatedAt={updatedAt} />
            </header>

            <section
              aria-label="Trip log filters"
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
            >
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative w-full lg:max-w-xl">
                  <label htmlFor="trip-search" className="sr-only">
                    Search trip logs
                  </label>
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    id="trip-search"
                    type="search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search by ID, customer, rider, location, payment, rating, or tip"
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-10 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-400 transition hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] dark:hover:bg-gray-700 dark:hover:text-gray-200"
                      aria-label="Clear trip search"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <div>
                    <label htmlFor="trip-status" className="sr-only">
                      Filter trips by status
                    </label>
                    <select
                      id="trip-status"
                      value={statusFilter}
                      onChange={updateFilter(setStatusFilter)}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 sm:w-40"
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="trip-time" className="sr-only">
                      Filter trips by time
                    </label>
                    <select
                      id="trip-time"
                      value={timeFilter}
                      onChange={updateFilter(setTimeFilter)}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 outline-none transition focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 sm:w-36"
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </section>

            {error && (
              <div
                role="alert"
                className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="inline-flex w-fit items-center gap-2 rounded-md border border-red-300 px-3 py-1.5 font-medium transition hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-800 dark:hover:bg-red-900/40"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  Try again
                </button>
              </div>
            )}

            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900" aria-labelledby="trip-table-title">
              <h2 id="trip-table-title" className="sr-only">Trip log records</h2>
              {loading && !hasApiRecords ? (
                <div className="overflow-x-auto">
                  <table className="min-w-[1180px] w-full text-left text-xs text-gray-500 dark:text-gray-400">
                    <caption className="sr-only">Loading trip logs</caption>
                    <TableHeader />
                    <SkeletonRows />
                  </table>
                </div>
              ) : !hasApiRecords ? (
                <EmptyState filtered={false} />
              ) : !hasFilteredRecords ? (
                <EmptyState filtered />
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-[1180px] w-full text-left text-xs text-gray-500 dark:text-gray-400">
                    <caption className="sr-only">Trip logs</caption>
                    <TableHeader />
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {pageTrips.map((trip, index) => (
                        <Fragment key={`${trip.id}-${index}`}>
                          <tr
                            className={`group transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
                              trip.cancellation ? "border-b-0" : ""
                            }`}
                          >
                            <td className="px-4 py-4 align-top font-mono text-[11px] text-slate-500 dark:text-slate-400">
                              <span title={trip.id}>{trip.shortId}</span>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="flex min-w-36 items-start gap-2">
                                <UsersRound className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                <span className="font-medium text-gray-700 dark:text-gray-200">{trip.customerName}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 align-top">
                              <div className="flex min-w-32 items-start gap-2 font-semibold text-gray-900 dark:text-gray-100">
                                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-[#FF8C00]" aria-hidden="true" />
                                <span>{formatTripDateTime(trip.requestedAt)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="flex min-w-40 items-start gap-2">
                                <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                                <span className="font-semibold text-gray-900 dark:text-gray-100">{trip.riderName}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 align-top">
                              <div className="flex min-w-32 items-start gap-2 font-semibold text-gray-900 dark:text-gray-100">
                                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-[#FF8C00]" aria-hidden="true" />
                                <span>{formatTripDateTime(trip.arrivalAt)}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 align-top">
                              <div className="flex min-w-32 items-start gap-2 font-semibold text-gray-900 dark:text-gray-100">
                                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-[#FF8C00]" aria-hidden="true" />
                                <span>{formatTripDateTime(trip.completedAt)}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-4 align-top">
                              <div className="flex min-w-32 items-start gap-2 font-semibold text-gray-900 dark:text-gray-100">
                                <CircleDollarSign className="mt-0.5 h-4 w-4 shrink-0 text-[#FF8C00]" aria-hidden="true" />
                                <span>{formatTripDateTime(trip.paidAt)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="min-w-28 space-y-1">
                                <span className="inline-flex items-center gap-1 font-medium text-gray-800 dark:text-gray-100">
                                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                                  {trip.rating}
                                </span>
                                <span className="block text-[11px] text-gray-500 dark:text-gray-400">Tip: {trip.tip}</span>
                              </div>
                            </td>
                            <td className="sticky right-0 whitespace-nowrap bg-white px-4 py-4 text-right align-top transition-colors group-hover:bg-gray-50 dark:bg-gray-900 dark:group-hover:bg-gray-800 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                              <div className="flex flex-col items-end gap-1.5">
                                {CANCELLABLE_STATUSES.has(
                                  String(trip.status || "").toLowerCase(),
                                ) ? (
                                  <button
                                    type="button"
                                    onClick={() => openCancelDialog(trip)}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-[11px] font-semibold text-red-600 transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
                                    title={`Cancel this trip (${trip.statusLabel})`}
                                  >
                                    <Ban className="h-3.5 w-3.5" aria-hidden="true" />
                                    Cancel
                                  </button>
                                ) : (
                                  <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                      statusClasses[
                                        String(trip.status || "").toLowerCase()
                                      ] || statusClasses.unknown
                                    }`}
                                  >
                                    {trip.statusLabel}
                                  </span>
                                )}
                                {trip.id !== "N/A" && (
                                  <Link
                                    to={`/trip-inspector/${trip.id}`}
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 transition-colors hover:text-[#FF8C00] focus:outline-none focus:ring-2 focus:ring-[#FF8C00] dark:text-gray-400"
                                    title={`Open ${trip.shortId} in the trip inspector`}
                                  >
                                    <FileSearch className="h-3.5 w-3.5" aria-hidden="true" />
                                    Inspect
                                  </Link>
                                )}
                              </div>
                            </td>
                          </tr>
                          {trip.cancellation && (
                            <tr>
                              <td colSpan={9} className="px-4 pb-3 pt-0">
                                {/* Who cancelled and why, under the row it explains. Below lg it is
                                    pinned to the scroll box's visible width (viewport minus main
                                    padding, cell padding and border) so it wraps instead of clipping. */}
                                <p className="sticky left-4 flex max-w-[calc(100vw-4rem-2px)] items-start gap-2 text-gray-600 dark:text-gray-300 sm:max-w-[calc(100vw-5rem-2px)] lg:static lg:max-w-3xl">
                                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                                  <span>
                                    {trip.cancellation.text}
                                    {trip.cancellation.at && (
                                      <span className="text-gray-400 dark:text-gray-500">
                                        {" · "}
                                        {formatTripDateTime(trip.cancellation.at)}
                                      </span>
                                    )}
                                  </span>
                                </p>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && hasFilteredRecords && (
                <nav
                  className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between"
                  aria-label="Trip log pagination"
                >
                  <span>
                    Showing {firstIndex + 1}–{firstIndex + pageTrips.length} of{" "}
                    {filteredTrips.length.toLocaleString()} matching trips
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FF8C00] disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </nav>
              )}
            </section>
          </div>
        </main>
      </div>

      {cancelSuccess && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 flex max-w-sm items-start gap-3 rounded-xl border border-green-200 bg-white p-4 shadow-lg dark:border-green-500/30 dark:bg-gray-900"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
          <p className="text-sm text-gray-700 dark:text-gray-200">{cancelSuccess}</p>
          <button
            type="button"
            onClick={() => setCancelSuccess(null)}
            className="ml-1 rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {cancelTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-trip-title"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900 max-h-[90vh] overflow-y-auto">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/10">
                <Ban className="h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
              </div>
              <div>
                <h2
                  id="cancel-trip-title"
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                >
                  Cancel this trip?
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {cancelTarget.customerName} · {cancelTarget.pickup} →{" "}
                  {cancelTarget.dropoff}
                </p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {cancelTarget.shortId} · currently {cancelTarget.statusLabel}
                </p>
              </div>
            </div>

            <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-400/10 dark:text-amber-300">
              The passenger and the rider will both be notified immediately, and
              the seats will be returned to the ride. This cannot be undone.
            </p>

            <label
              htmlFor="cancel-reason"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Reason <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              maxLength={300}
              rows={3}
              disabled={cancelling}
              placeholder="e.g. Rider unreachable after accepting; passenger stranded"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-[#FF8C00] focus:outline-none focus:ring-1 focus:ring-[#FF8C00] disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
              Shown word-for-word to the passenger and the rider, so write it for
              them to read. {300 - cancelReason.length} characters left.
            </p>

            {cancelError && (
              <p
                role="alert"
                className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-400"
              >
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {cancelError}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCancelDialog}
                disabled={cancelling}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Keep trip
              </button>
              <button
                type="button"
                onClick={confirmCancel}
                disabled={cancelling}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-60"
              >
                {cancelling && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {cancelling ? "Cancelling…" : "Cancel trip"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trips;